// "Project names" - the names that identify this site in an email subject: the
// sweep's identity signal (design-email-ingestion-redesign.md section 5.2).
//
// A form because it can't be derived: in "The Range, Challenger 1, Avonmouth" the
// site is "Challenger 1" / "Avonmouth", while "The Range" is a chain that would
// match every site it owns.
import { useEffect, useRef, useState } from "react";

import {
  MAX_NAMES,
  MIN_NAME_CHARS,
  useSetEmailProjectNames,
} from "../api/hooks/useEmailProjectNames";
import styles from "./ProjectNamesCard.module.css";

export const ProjectNamesCard = ({
  projectId,
  names,
  suggestions = [],
}: {
  projectId: string;
  names: string[];
  // Derived by the BACKEND so it matches the seed script. Saved automatically while
  // the project has no names, since without one the sweep files nothing.
  suggestions?: string[];
}) => {
  const [draft, setDraft] = useState("");
  const setNames = useSetEmailProjectNames(projectId);
  const saving = setNames.isLoading;

  // Once per mount, so removing the last name doesn't immediately re-add it.
  const autoApplied = useRef(false);
  const [applied, setApplied] = useState<string[]>([]);
  useEffect(() => {
    if (autoApplied.current || names.length > 0 || suggestions.length === 0) return;
    autoApplied.current = true;
    setNames.mutate({ names: suggestions }, { onSuccess: (r) => setApplied(r.names) });
  }, [names.length, suggestions, setNames]);

  const lower = new Set(names.map((n) => n.toLowerCase()));
  const unusedSuggestions = suggestions.filter((n) => !lower.has(n.toLowerCase()));

  const trimmed = draft.trim();
  const duplicate = names.some((n) => n.toLowerCase() === trimmed.toLowerCase());
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_NAME_CHARS;
  const full = names.length >= MAX_NAMES;
  const canAdd = trimmed.length >= MIN_NAME_CHARS && !duplicate && !full && !saving;

  const commit = (next: string[]) => {
    setNames.mutate(
      { names: next },
      { onSuccess: () => setDraft("") },
    );
  };

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.title}>Project names</h3>
        {/* Count only once there is something to count; the ceiling only when close. */}
        {names.length > 0 && (
          <span className={styles.count}>
            {names.length === 1 ? "1 name" : `${names.length} names`}
            {names.length >= MAX_NAMES - 2 ? ` (max ${MAX_NAMES})` : ""}
          </span>
        )}
      </header>

      {names.length === 0 && (
        <p className={styles.empty}>
          No names yet. Automatic ingestion needs at least one: without
          it the sweep cannot recognise this site by name in a subject line.
        </p>
      )}

      <p className={styles.description}>
        The distinctive part of this site&apos;s name, as it appears in an email
        subject &mdash; a site code, a unit or park name, or the town. Add the
        parts that identify <em>this site</em>, not the customer: a chain name
        matches every site that chain owns.
      </p>

      {names.length > 0 && applied.length > 0 && (
        <p className={styles.hint}>
          Added from the site name. Remove any that don&apos;t identify this site.
        </p>
      )}

      {names.length > 0 && (
        <div className={styles.names}>
          {names.map((name) => (
            <span key={name} className={styles.chip}>
              {name}
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remove ${name}`}
                disabled={saving}
                onClick={() => commit(names.filter((n) => n !== name))}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Shown only while nothing is configured, so they never nag a set-up project. */}
      {names.length === 0 && unusedSuggestions.length > 0 && (
        <div className={styles.suggestions}>
          <span className={styles.suggestLabel}>Suggested:</span>
          {unusedSuggestions.map((name) => (
            <button
              key={name}
              type="button"
              className={styles.suggestion}
              disabled={saving}
              onClick={() => commit([...names, name])}
            >
              + {name}
            </button>
          ))}
        </div>
      )}

      <div className={styles.entry}>
        <input
          className={styles.input}
          value={draft}
          disabled={saving || full}
          placeholder={full ? `Limit of ${MAX_NAMES} reached` : "e.g. Challenger 1"}
          aria-label="Add a project name"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canAdd) {
              e.preventDefault();
              commit([...names, trimmed]);
            }
          }}
        />
        <button
          type="button"
          className={styles.button}
          disabled={!canAdd}
          onClick={() => commit([...names, trimmed])}
        >
          {saving ? "Saving…" : "Add"}
        </button>
      </div>

      {tooShort && (
        <p className={styles.hint}>
          A name must be at least {MIN_NAME_CHARS} characters &mdash; shorter than
          that matches inside ordinary words.
        </p>
      )}
      {duplicate && <p className={styles.hint}>Already added.</p>}
      {/* A silent failure would read as a saved name the sweep never sees. */}
      {setNames.isError && (
        <p className={styles.error}>
          Could not save: {(setNames.error as Error)?.message ?? "unknown error"}
        </p>
      )}
      {setNames.data && setNames.data.dropped.length > 0 && (
        <p className={styles.hint}>
          Not added: {setNames.data.dropped.join(", ")}
        </p>
      )}
    </section>
  );
};
